<template>
  <BaseModal
    :model-value="modelValue"
    :title="tpl?.name || '模版详情'"
    :subtitle="subtitle"
    size="lg"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('close')"
  >
    <div class="tpl-preview-body">
      <div class="tpl-preview-img-box">
        <img
          v-if="thumb"
          :src="thumb"
          :alt="tpl?.name"
          class="tpl-preview-img"
        />
        <div v-else-if="hasPdfOrigin" class="tpl-preview-fallback tpl-preview-pdf-hint">
          <span class="pdf-icon">PDF</span>
          <span>该模版为 PDF 源文件，暂无封面图</span>
        </div>
        <div v-else class="tpl-preview-fallback">暂无缩略图预览</div>
      </div>

    </div>

    <template #footer>
      <button type="button" class="btn" @click="$emit('update:modelValue', false)">关闭</button>
      <button
        v-if="canEditMaster && !pendingMaster"
        type="button"
        class="btn"
        :disabled="loading"
        @click="$emit('edit-template', tpl)"
      >
        编辑母模版
      </button>
      <button
        v-if="!pendingMaster"
        type="button"
        class="btn-primary use-tpl-btn"
        :disabled="loading"
        @click="$emit('use-template', tpl)"
      >
        <span v-if="loading" class="btn-spinner" aria-hidden="true" />
        {{ loading ? '正在打开…' : '使用此模版创建设计' }}
      </button>
      <button
        v-else-if="canEditMaster"
        type="button"
        class="btn-primary use-tpl-btn"
        :disabled="loading"
        @click="$emit('edit-template', tpl)"
      >
        <span v-if="loading" class="btn-spinner" aria-hidden="true" />
        {{ loading ? '正在打开…' : '继续编辑并保存母模版' }}
      </button>
    </template>
  </BaseModal>
</template>

<script setup>
import { computed } from 'vue';
import BaseModal from '@/components/common/BaseModal.vue';
import { DEFAULT_TEMPLATE_THUMB } from '@/utils/platformHelpers';
import { isPdfAssetUrl } from '@/utils/templateThumb';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  tpl: {
    type: Object,
    default: null,
  },
  thumb: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  canEditMaster: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['update:modelValue', 'close', 'use-template', 'edit-template']);

const subtitle = computed(() => {
  if (!props.tpl) return '';
  if (props.tpl.pendingMasterEdit) return '待保存母模版 · 编辑确认后才会成为正式模版';
  return `${props.tpl.category || '模版'} · ${props.tpl.size || ''}`;
});

const pendingMaster = computed(() => Boolean(props.tpl?.pendingMasterEdit));

const hasPdfOrigin = computed(() => {
  if (!props.tpl) return false;
  const thumb = String(props.thumb || '').trim();
  if (thumb && thumb !== DEFAULT_TEMPLATE_THUMB && !isPdfAssetUrl(thumb)) return false;
  return isPdfAssetUrl(props.tpl.originFileUrl);
});
</script>

<style scoped>
.tpl-preview-body {
  width: 100%;
}

.tpl-preview-img-box {
  background: #f8fafc;
  background-image: radial-gradient(#e2e8f0 1.2px, transparent 1.2px);
  background-size: 12px 12px;
  border-radius: 12px;
  min-height: 320px;
  max-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
}

.tpl-preview-img {
  max-width: 100%;
  max-height: 480px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
}

.tpl-preview-fallback {
  color: var(--text-muted, #94a3b8);
  font-size: 13px;
}

.tpl-preview-pdf-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  line-height: 1.5;
}

.tpl-preview-pdf-hint .pdf-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  background: #fee2e2;
  color: #dc2626;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.btn {
  padding: 8px 18px;
  border: 1px solid var(--border-color, #cbd5e1);
  border-radius: 8px;
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn:hover {
  background: #f1f5f9;
}

.btn-primary {
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  background: var(--color-primary);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
}

.btn-primary:hover:not(:disabled) {
  background: #6d28d9;
}

.btn-primary:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.btn-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  display: inline-block;
  margin-right: 6px;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
